import React, { useCallback, useState } from 'react';
// icons
import { MdMood } from 'react-icons/md';

import { Tooltip, Popover } from '@mui/material';
import { UseMutationResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import TextInput from '../../../../../components/inputs/textInput';
import AampeTooltip from '../../../../../components/shared/AampeTooltip/AampeTooltip';
import EmojiPicker from '../../../../../components/shared/EmojiPicker/EmojiPicker';
import styled from 'styled-components';

// theme
import aampeTheme from '../../../../../constants/aampeTheme';
import ICONS from '../../../../../constants/icons';

// components
import AlternateLabelSection from '../../../../../editors/PushNotificationEditor/Sidebar/AlternateLabelSection';

import { BaseError } from 'helpers/BaseError';

// models
import { AlternateTextMinHoverLength } from '../../../../../models/alternate.model';
import { VariantWithLabel } from 'models/variant.model';

interface AlternateListItemContentProps {
    alternateText: string;
    setAlternateText: (value: string) => void;
    setSelectedAlternate: (value: string | null) => void;
    setAnchorEl: (value: Element | null) => void;
    alternateData: VariantWithLabel;
    selected?: boolean;
    anchorEl: Element | null;
    saveChanges: (e: any) => void;
    inputRef?: any;
    togglePicker?: (e: any) => void;
    isEditable: boolean;
    listView?: boolean;
    error?: string;
    onEmojiSelect?: any;
    isMasterVariant?: boolean;
    alternateDeleteMutation: UseMutationResult<
        AxiosResponse<string, any>,
        BaseError,
        string,
        unknown
    >;
    isEmojiSelectingRef?: React.MutableRefObject<boolean>;
}

const AlternateListItemContent: React.FC<
    React.PropsWithChildren<AlternateListItemContentProps>
> = ({
    alternateText,
    setAlternateText,
    setSelectedAlternate,
    setAnchorEl,
    alternateData,
    selected,
    anchorEl,
    saveChanges,
    inputRef,
    togglePicker,
    isEditable,
    error,
    onEmojiSelect,
    isMasterVariant,
    alternateDeleteMutation,
    isEmojiSelectingRef,
}) => {
    const [showDeleteIcon, setShowDeleteIcon] = useState(false);

    const handleClick = useCallback(() => {
        const element = document.getElementById(
            `input-anchor-el-${alternateData.id}`,
        );
        setAnchorEl(element);
    }, [alternateData, setAnchorEl]);

    const handleAlternateClick = () => {
        if (isEditable) {
            setSelectedAlternate(alternateData.id);
            setAnchorEl(null);
        }
    };
    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            saveChangesAndDismiss(e);
        }
    };

    const saveChangesAndDismiss = (
        e:
            | React.KeyboardEvent<HTMLInputElement>
            | React.FocusEvent<HTMLInputElement>,
    ) => {
        e.preventDefault();
        // Skip saving if emoji selection just occurred - the emoji handler already saved
        if (isEmojiSelectingRef?.current) {
            return;
        }
        saveChanges(e);
        if (!anchorEl) {
            setSelectedAlternate(null);
        }
    };

    const onEmojiIconClick = useCallback(
        (e: any) => {
            if (togglePicker) togglePicker(e);
            handleClick();
        },
        [handleClick, togglePicker],
    );

    return (
        <Container
            selected={selected}
            id={`input-anchor-el-${alternateData.id}`}
            onMouseEnter={() => isEditable && setShowDeleteIcon(true)}
            onMouseLeave={() => isEditable && setShowDeleteIcon(false)}
        >
            {selected && isEditable ? (
                <>
                    <Tooltip
                        title={
                            alternateText.length > AlternateTextMinHoverLength
                                ? alternateText
                                : ''
                        }
                        disableHoverListener
                        placement="top"
                        arrow={true}
                    >
                        {/* Do not remove empty span for proper styling */}
                        <span>
                            <StyledTextInput
                                value={alternateText}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>,
                                ) => setAlternateText(e.target.value)}
                                inputRef={inputRef}
                                trailingIcon={<HoverableMdMood size={16} />}
                                trailingIconAction={onEmojiIconClick}
                                inputStyle={{
                                    width: '100%',
                                }}
                                onKeyDown={handleKeyPress}
                                onBlur={saveChangesAndDismiss}
                                autoFocus={true}
                                error={error}
                                touched={!!error}
                            />
                            <StyledPopover
                                open={!!anchorEl}
                                anchorEl={anchorEl}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                            >
                                <EmojiPicker onEmojiClick={onEmojiSelect} />
                            </StyledPopover>
                        </span>
                    </Tooltip>
                </>
            ) : (
                <>
                    {' '}
                    <AlternateWrapper>
                        <AampeTooltip
                            title={
                                alternateText.length >
                                AlternateTextMinHoverLength
                                    ? alternateText
                                    : ''
                            }
                            arrow={true}
                            placement="top"
                        >
                            <AlternateTextContainer>
                                {isMasterVariant && (
                                    <AampeTooltip
                                        title="The first alternate you create becomes the master component. You can edit it, but it cannot be deleted."
                                        arrow={true}
                                        placement="top"
                                    >
                                        <LockIconContainer>
                                            <ICONS.alternateLock />
                                        </LockIconContainer>
                                    </AampeTooltip>
                                )}
                                <EllipsisText
                                    onClick={handleAlternateClick}
                                    onFocus={() => {
                                        isEditable &&
                                            setSelectedAlternate(
                                                alternateData.id,
                                            );
                                    }}
                                    tabIndex={1}
                                >
                                    {alternateText}
                                </EllipsisText>
                            </AlternateTextContainer>
                        </AampeTooltip>
                        <SelectContainer>
                            <AlternateLabelSection
                                data={alternateData}
                                isEditable={isEditable}
                            />
                        </SelectContainer>
                    </AlternateWrapper>
                    <IconContainer>
                        {showDeleteIcon && !isMasterVariant && isEditable && (
                            <div
                                onClick={() =>
                                    alternateDeleteMutation.mutate(
                                        alternateData.id,
                                    )
                                }
                            >
                                <StyledDeleteIcon />
                            </div>
                        )}
                    </IconContainer>
                </>
            )}
        </Container>
    );
};

const Container = styled.div<any>((props) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
    padding: !props.selected ? aampeTheme.spacing(0, 1, 0, 2) : 0,
}));

const AlternateWrapper = styled.div(() => ({
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
}));

const HoverableMdMood = styled(MdMood)`
    font-size: 16px;
    margin-top: ${aampeTheme.spacing(0.5)};
    color: ${aampeTheme.colors.gray950};
    transition: color 0.3s, background 0.3s;

    &:hover {
        color: black;
        background: ${aampeTheme.colors.backgroundWG02};
        border-radius: ${aampeTheme.borderRadius.default};
    }
`;
const EllipsisText = styled.div(() => ({
    width: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: aampeTheme.spacing(1, 0),
    color: aampeTheme.colors.gray950,
    fontWeight: 400,
}));
const StyledTextInput = styled(TextInput)<any>((props) => ({
    '& .MuiInputBase-root': {
        backgroundColor: aampeTheme.colors.white,
        borderRadius: aampeTheme.borderRadius.default,
        borderColor: aampeTheme.colors.blue1000,
        minHeight: '32px',
        height: '32px',
        width: '510px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: aampeTheme.spacing4(5),
    },
    '& .MuiOutlinedInput-input': {
        padding: '0',
    },
    '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
            border: props.error
                ? `1px solid ${aampeTheme.colors.error500}`
                : `1px solid ${aampeTheme.colors.blue1000}`,
        },
    },
}));
const StyledPopover = styled(Popover)({
    position: 'absolute',
});

const LockIconContainer = styled.span({
    width: '20px',
    height: ' 20px',
    marginRight: aampeTheme.spacing4(1),
    cursor: 'pointer',
});

const AlternateTextContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    width: '250px',
    flex: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: aampeTheme.spacing4(1, 2, 1, 0),
});

const StyledDeleteIcon = styled(ICONS.trash)({
    color: aampeTheme.colors.textAccessibleWG01,
    width: '20px',
    height: '20px',
    cursor: 'pointer',
});

const IconContainer = styled.div({
    width: '20px',
    height: '20px',
});

const SelectContainer = styled.div({
    width: '220px',
    overflow: 'auto',
});
export default AlternateListItemContent;
